
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAffiliateInfo } from '@/hooks/useAffiliate';
import { affiliateService } from '@/services/affiliateService';
import { useQueryClient } from '@tanstack/react-query';
import { Shield, Key } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface AiPropFormValues {
  aiPropRefLink: string;
}

export const AffiliateSettings = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: affiliateInfo, isLoading } = useAffiliateInfo();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  
  const form = useForm<AiPropFormValues>({
    defaultValues: {
      aiPropRefLink: affiliateInfo?.ai_prop_ref_link || ''
    }
  });
  
  React.useEffect(() => {
    if (affiliateInfo && !isLoading) {
      form.reset({
        aiPropRefLink: affiliateInfo.ai_prop_ref_link || ''
      });
    }
  }, [affiliateInfo, isLoading, form]);
  
  const handleSubmit = async (values: AiPropFormValues) => {
    try {
      setIsSubmitting(true);
      
      if (!affiliateInfo?.id) {
        toast.error("Unable to update: Affiliate information not found");
        return;
      }
      
      const success = await affiliateService.updateAiPropRefLink(affiliateInfo.id, values.aiPropRefLink);
      
      if (success) {
        toast.success("AI Prop referral link updated successfully");
        queryClient.invalidateQueries({ queryKey: ['affiliateInfo'] });
      } else {
        toast.error("Failed to update AI Prop referral link");
      }
    } catch (error) {
      console.error('Error updating AI Prop referral link:', error);
      toast.error("An error occurred while updating AI Prop referral link");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading) {
    return <div className="">Loading settings...</div>;
  }

  return (
    <div className="space-y-4 md:space-y-8">
      <h1 className="text-xl md:text-3xl font-bold">Affiliate Settings</h1>
      
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Card className="card-scheme-ocean-blue">
          <CardHeader className={isMobile ? "p-4" : "p-6"}>
            <div className="flex items-center gap-2">
              <Key className="h-4 w-4 md:h-5 md:w-5 text-botscheme-ocean-blue" />
              <CardTitle className={isMobile ? "text-lg" : "text-2xl"}>AI Prop Referral Link</CardTitle>
            </div>
            <CardDescription className={isMobile ? "text-xs" : "text-sm"}>
              Enter your AI Prop referral link here. This link will be used when users click on the AI Prop challenge button.
            </CardDescription>
          </CardHeader>
          <CardContent className={isMobile ? "px-4" : "px-6"}>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="aiPropRefLink">AI Prop Referral Link</Label>
                <Input 
                  id="aiPropRefLink"
                  placeholder="https://aiprop.com/ref/your-link"
                  {...form.register('aiPropRefLink')}
                  className="bg-background/80 border"
                />
                <p className={`${isMobile ? "text-xs" : "text-sm"} text-muted-foreground`}>
                  This is the referral link provided to you by AI Prop. Users who click on the AI Prop challenge button will be directed to this link.
                </p>
              </div>
            </div>
          </CardContent>
          <CardFooter className={isMobile ? "p-4 pt-0" : "p-6 pt-0"}>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="bg-botscheme-ocean-blue hover:bg-botscheme-ocean-blue/90"
              size={isMobile ? "sm" : "default"}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </CardFooter>
        </Card>
      </form>
      
      <Card className="card-scheme-royal-purple">
        <CardHeader className={isMobile ? "p-4" : "p-6"}>
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 md:h-5 md:w-5 text-botscheme-royal-purple" />
            <CardTitle className={isMobile ? "text-lg" : "text-2xl"}>Affiliate Account</CardTitle>
          </div>
          <CardDescription className={isMobile ? "text-xs" : "text-sm"}>
            Manage your affiliate account settings
          </CardDescription>
        </CardHeader>
        <CardContent className={isMobile ? "p-4 pt-0" : "p-6 pt-0"}>
          <div className="space-y-3 md:space-y-4">
            <div className="grid grid-cols-2 gap-2 md:gap-4">
              <div>
                <Label className={isMobile ? "text-xs" : "text-sm"}>Status</Label>
                <p className={`mt-1 ${isMobile ? "text-xs" : "text-sm"}`}>
                  {affiliateInfo?.status === 'active' ? (
                    <span className="text-botscheme-emerald-green font-medium">Active</span>
                  ) : (
                    <span className="text-botscheme-amber-gold font-medium">Pending</span>
                  )}
                </p>
              </div>
              <div>
                <Label className={isMobile ? "text-xs" : "text-sm"}>Commission Rate</Label>
                <p className={`mt-1 ${isMobile ? "text-xs" : "text-sm"}`}>{(affiliateInfo?.commission_rate || 0) * 100}%</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AffiliateSettings;
